import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import emitter, { EmitterEventTypes } from '~/lib/emitter';
import useIsAssistantEnabled from '~/lib/useIsAssistantEnabled';
import { trackAmplitudeEvent } from '~/lib/AmplitudeTracking';

const collapsedStorageKey = id => `assistant-banner-collapsed-${id}`;

const AssistantBanner = ({
    id,
    prompt,
    title = "Have your Assistant handle this for you",
    description = "Tell Breely's Assistant what you want to offer, how it should be booked, who or what can be booked, when it's available, and it'll set up the event for you.",
    ctaLabel = 'Setup with Assistant →',
    className = '',
}) => {
    const isAssistantEnabled = useIsAssistantEnabled();
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return localStorage.getItem(collapsedStorageKey(id)) === 'true';
        } catch {
            return false;
        }
    });

    if (!isAssistantEnabled) {
        return null;
    }

    const openAssistant = () => {
        trackAmplitudeEvent('Assistant Opened from Banner', {
            banner_id: id,
            prompt,
        });
        emitter.emit(EmitterEventTypes.OPEN_ASSISTANT_WITH_PROMPT, { prompt });
    };

    const collapse = () => {
        try {
            localStorage.setItem(collapsedStorageKey(id), 'true');
        } catch {
            // ignore storage failures (e.g. private browsing)
        }
        setCollapsed(true);
    };

    if (collapsed) {
        return (
            <button
                type="button"
                className={`inline-flex items-center gap-x-1.5 rounded-full border border-primary-700/30 bg-primary-100 px-[18px] py-3 drop-shadow-[0px_0px_4px_rgba(0,0,0,0.06)] ${className}`}
                onClick={openAssistant}
            >
                <span className="flex items-center rounded-full bg-green-500/20 p-0.5">
                    <span className="relative flex size-1.5">
                        <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-500 opacity-75" />
                        <span className="relative inline-flex size-1.5 rounded-full bg-green-500" />
                    </span>
                </span>
                <span className="whitespace-nowrap text-sm font-semibold leading-none tracking-[0.28px] text-primary-700">
                    {ctaLabel}
                </span>
            </button>
        );
    }

    return (
        <div
            className={`relative flex max-w-2xl items-start gap-3 rounded-[22px] border border-primary-700/30 bg-primary-100 p-6 pr-10 shadow-[0px_0px_8px_4px_rgba(0,0,0,0.04)] ${className}`}
        >
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <p className="w-full pb-1 text-[17px] font-medium leading-[17px] text-primary-700">
                    {title}
                </p>
                <p className="w-full pb-3 text-sm leading-5 tracking-[0.14px] text-primary-700/80">
                    {description}
                </p>
                <button
                    type="button"
                    className="btn-pill font-semibold tracking-[0.28px]"
                    onClick={openAssistant}
                >
                    {ctaLabel}
                </button>
            </div>
            <button
                type="button"
                className="absolute right-3 top-3 flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary-700/10 hover:bg-primary-700/20"
                onClick={collapse}
            >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon
                    className="size-3 text-primary-700/80"
                    strokeWidth={2}
                />
            </button>
        </div>
    );
};

export default AssistantBanner;
